/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/mv_slice.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Returns a subset of the multivalued field using the start and end index values. Indexes are 0-based.
 * This is most useful when reading from a function that emits multivalued columns
 * in a known order like `SPLIT` or `MV_SORT`.
 *
 * @example
 * row a = [1, 2, 2, 3]
 * | eval a1 = mv_slice(a, 1), a2 = mv_slice(a, 2, 3)
 *
 * @example
 * row a = [1, 2, 2, 3]
 * | eval a1 = mv_slice(a, -2), a2 = mv_slice(a, -3, -1)
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'mv_slice',
  signatures: [
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'field',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'flattened',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geohash',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'geohash',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geohex',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'geohex',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geotile',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'geotile',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
        {
          name: 'start',
          type: 'integer',
          optional: false,
        },
        {
          name: 'end',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'version',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
